import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { listenDeletionRequests, processDeletionRequest, formatTime, toDate, phoneHref, waHref } from '../services/firebase';
import { useLang } from '../lang/LangContext';

const REASON_LABELS = {
  not_using: 'Not using the app anymore',
  switch_device: 'Switching to a new device or number',
  privacy_concerns: 'Privacy / personal data concerns',
  too_many_notifications: 'Too many notifications',
  pentest: 'Security testing / pentest account',
  other: 'Other reasons',
};

export default function DeletionRequests({ isSuperAdmin = false }) {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState('');
  const [filter, setFilter] = useState('pending'); // 'pending' | 'fulfilled' | 'rejected' | 'all'
  const [copiedUid, setCopiedUid] = useState('');

  useEffect(() => listenDeletionRequests(setRows), []);

  const act = async (requestId, action, phone) => {
    if (!isSuperAdmin) {
      toast.error('Processing deletion requests requires Super Admin privileges');
      return;
    }
    if (action === 'fulfill') {
      const ok = window.confirm(
        `Are you sure you want to permanently delete account for +91 ${phone}?\n\nThis will soft-redact personal data (name, phone, photo) from our systems while retaining anonymized operational ride records for DPDP compliance.`
      );
      if (!ok) return;
    }
    setBusyId(requestId);
    try {
      await processDeletionRequest(requestId, action);
      toast.success(action === 'reject' ? 'Request Rejected' : 'Account Deleted & PII Redacted Successfully');
    } catch (e) {
      toast.error(e?.message || 'Failed to process request');
    } finally {
      setBusyId('');
    }
  };

  const copyUid = (uid) => {
    if (!uid) return;
    navigator.clipboard?.writeText(uid);
    setCopiedUid(uid);
    toast.success('UID copied to clipboard');
    setTimeout(() => setCopiedUid(''), 2000);
  };

  const pending = useMemo(() => rows.filter((r) => r.status === 'pending'), [rows]);
  const fulfilled = useMemo(() => rows.filter((r) => r.status === 'fulfilled'), [rows]);
  const rejected = useMemo(() => rows.filter((r) => r.status === 'rejected'), [rows]);

  // Derived real DPDP statutory SLA compliance metric (72h limit)
  const slaMetrics = useMemo(() => {
    if (rows.length === 0) return { rate: '—', sub: 'No deletion requests recorded' };
    const now = Date.now();
    const MAX_SLA_MS = 72 * 60 * 60 * 1000; // 72h statutory SLA

    let compliantCount = 0;
    let breachedCount = 0;

    rows.forEach((r) => {
      const created = toDate(r.createdAt);
      if (!created) return;
      const createdMs = created.getTime();

      if (r.status === 'fulfilled' || r.status === 'rejected') {
        const processed = toDate(r.processedAt || r.fulfilledAt || r.rejectedAt || r.updatedAt) || new Date();
        const duration = processed.getTime() - createdMs;
        if (duration <= MAX_SLA_MS) {
          compliantCount += 1;
        } else {
          breachedCount += 1;
        }
      } else if (r.status === 'pending') {
        if (now - createdMs <= MAX_SLA_MS) {
          compliantCount += 1;
        } else {
          breachedCount += 1;
        }
      }
    });

    const total = compliantCount + breachedCount;
    if (total === 0) return { rate: '—', sub: 'No valid timestamp data' };
    const rate = Math.round((compliantCount / total) * 100);
    return {
      rate: `${rate}%`,
      sub: breachedCount > 0 ? `${breachedCount} breached (>72h SLA)` : '100% on-time within 72h SLA',
    };
  }, [rows]);

  const displayedRows = useMemo(() => {
    if (filter === 'pending') return pending;
    if (filter === 'fulfilled') return fulfilled;
    if (filter === 'rejected') return rejected;
    return rows;
  }, [filter, pending, fulfilled, rejected, rows]);

  return (
    <div>
      {/* 4 Summary Stat Metric Cards */}
      <div className="stat-grid">
        <div className="stat-card orange">
          <div className="stat-label">PENDING REVIEW</div>
          <div className="stat-value orange">{pending.length}</div>
          <div className="stat-sub">Action required within 24h</div>
        </div>

        <div className="stat-card green">
          <div className="stat-label">FULFILLED & REDACTED</div>
          <div className="stat-value green">{fulfilled.length}</div>
          <div className="stat-sub">PII purged / DPDP compliant</div>
        </div>

        <div className="stat-card red">
          <div className="stat-label">REJECTED REQUESTS</div>
          <div className="stat-value red">{rejected.length}</div>
          <div className="stat-sub">Failed verification / cancelled</div>
        </div>

        <div className="stat-card blue">
          <div className="stat-label">DPDP SLA COMPLIANCE</div>
          <div className="stat-value blue">{slaMetrics.rate}</div>
          <div className="stat-sub">{slaMetrics.sub}</div>
        </div>
      </div>

      {/* Compliance Info Banner */}
      <div
        className="card"
        style={{
          background: 'linear-gradient(135deg, rgba(0, 168, 107, 0.08), rgba(59, 130, 246, 0.06))',
          borderColor: 'rgba(0, 168, 107, 0.25)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 14,
        }}
      >
        <div style={{ fontSize: 24, flexShrink: 0 }}>🛡️</div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#ffffff', marginBottom: 3 }}>
            DPDP Act 2023 Erasure Guidelines
          </div>
          <div style={{ fontSize: 12.5, color: '#94a3b8', lineHeight: 1.5 }}>
            Before fulfilling a deletion request, verify user phone identity. Fulfilling permanently removes all personal identifiable information (PII) including name, phone, and profile photos from Auth & Firestore. Past ride operational logs are anonymized and retained for financial and safety records.
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="preset-pills-row" style={{ marginTop: 20, marginBottom: 16 }}>
        <button
          type="button"
          className={`preset-pill${filter === 'pending' ? ' active' : ''}`}
          onClick={() => setFilter('pending')}
        >
          <span>⏳ {t('Pending Review')}</span>
          <span style={{ background: filter === 'pending' ? 'rgba(0, 168, 107, 0.3)' : 'rgba(255, 255, 255, 0.1)', padding: '1px 6px', borderRadius: 10, fontSize: 11 }}>
            {pending.length}
          </span>
        </button>

        <button
          type="button"
          className={`preset-pill${filter === 'fulfilled' ? ' active' : ''}`}
          onClick={() => setFilter('fulfilled')}
        >
          <span>✅ {t('Fulfilled')}</span>
          <span style={{ background: 'rgba(255, 255, 255, 0.1)', padding: '1px 6px', borderRadius: 10, fontSize: 11 }}>
            {fulfilled.length}
          </span>
        </button>

        <button
          type="button"
          className={`preset-pill${filter === 'rejected' ? ' active' : ''}`}
          onClick={() => setFilter('rejected')}
        >
          <span>✕ {t('Rejected')}</span>
          <span style={{ background: 'rgba(255, 255, 255, 0.1)', padding: '1px 6px', borderRadius: 10, fontSize: 11 }}>
            {rejected.length}
          </span>
        </button>

        <button
          type="button"
          className={`preset-pill${filter === 'all' ? ' active' : ''}`}
          onClick={() => setFilter('all')}
        >
          <span>All Logs ({rows.length})</span>
        </button>
      </div>

      {/* Main Request Cards / Table */}
      {filter === 'pending' ? (
        <div>
          {pending.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>🎉</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#ffffff' }}>{t('Zero Pending Deletion Requests')}</div>
              <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>
                All user account erasure requests have been processed and fulfilled.
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 16 }}>
              {pending.map((r) => {
                const tel = phoneHref(r.phone);
                const wa = waHref(r.phone, 'Namaste, MyTownly Data Privacy Desk here regarding your account deletion request.');
                const isCaptain = r.role === 'driver' || r.role === 'captain';
                return (
                  <div
                    key={r.id}
                    className="card"
                    style={{
                      marginBottom: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                    }}
                  >
                    <div>
                      {/* Top Header with Role & Phone */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span
                            className="badge"
                            style={{
                              background: isCaptain ? 'rgba(0, 168, 107, 0.18)' : 'rgba(59, 130, 246, 0.18)',
                              color: isCaptain ? '#34d399' : '#60a5fa',
                              border: `1px solid ${isCaptain ? 'rgba(0, 168, 107, 0.35)' : 'rgba(59, 130, 246, 0.35)'}`,
                              textTransform: 'capitalize',
                            }}
                          >
                            {isCaptain ? '👨‍✈️ Captain' : '👤 Passenger'}
                          </span>
                          <span style={{ fontWeight: 700, fontSize: 15, color: '#ffffff' }}>
                            +91 {r.phone}
                          </span>
                        </div>

                        {/* Quick Phone / WhatsApp Verify Actions */}
                        <div style={{ display: 'flex', gap: 6 }}>
                          {tel && (
                            <a href={tel} className="btn btn-ghost btn-sm" title="Call to verify identity">📞</a>
                          )}
                          {wa && (
                            <a href={wa} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" title="WhatsApp Verify" style={{ color: '#34d399' }}>💬</a>
                          )}
                        </div>
                      </div>

                      {/* Monospace UID Pill with Copy */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          background: 'rgba(0, 0, 0, 0.3)',
                          padding: '6px 10px',
                          borderRadius: 8,
                          border: '1px solid rgba(255, 255, 255, 0.06)',
                          marginBottom: 12,
                        }}
                      >
                        <span className="mono" style={{ fontSize: 11, color: '#94a3b8' }}>
                          uid: {r.uid || '—'}
                        </span>
                        <button
                          type="button"
                          className="linkish"
                          style={{ fontSize: 11, color: copiedUid === r.uid ? '#34d399' : 'var(--green)' }}
                          onClick={() => copyUid(r.uid)}
                        >
                          {copiedUid === r.uid ? 'Copied ✔' : 'Copy'}
                        </button>
                      </div>

                      {/* Stated Reason */}
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4, letterSpacing: '0.5px' }}>
                          Stated Reason:
                        </div>
                        <div style={{ fontSize: 13, color: '#f8fafc', fontWeight: 500 }}>
                          {REASON_LABELS[r.reason] || r.reason || 'Not specified'}
                        </div>
                        {r.details && (
                          <div
                            style={{
                              marginTop: 6,
                              fontSize: 12,
                              color: '#94a3b8',
                              background: 'rgba(255, 255, 255, 0.03)',
                              padding: '8px 10px',
                              borderRadius: 6,
                              fontStyle: 'italic',
                            }}
                          >
                            "{r.details}"
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'grid', gridTemplateColumns: isSuperAdmin ? '1.4fr 1fr' : '1fr', gap: 8, paddingTop: 12, borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
                      {isSuperAdmin ? (
                        <>
                          <button
                            type="button"
                            className="btn"
                            style={{
                              background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                              color: '#ffffff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 6,
                              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)',
                            }}
                            disabled={busyId === r.id}
                            onClick={() => act(r.id, 'fulfill', r.phone)}
                          >
                            <span>🗑️</span>
                            <span>{busyId === r.id ? 'Processing…' : 'Fulfill & Redact'}</span>
                          </button>

                          <button
                            type="button"
                            className="btn btn-ghost"
                            disabled={busyId === r.id}
                            onClick={() => act(r.id, 'reject', r.phone)}
                          >
                            ✕ Reject
                          </button>
                        </>
                      ) : (
                        <div className="muted" style={{ fontSize: 12, textAlign: 'center', padding: '6px 0' }}>
                          🔒 Super Admin required to fulfill / reject
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        /* Audit History Table for Fulfilled / Rejected / All */
        <div className="card">
          <div className="card-title">
            <span>Audit Log History ({displayedRows.length})</span>
          </div>

          {displayedRows.length === 0 ? (
            <div className="empty">{t('No records matching filter')}</div>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>STATUS</th>
                    <th>ROLE & USER</th>
                    <th>PHONE</th>
                    <th>UID</th>
                    <th>REASON</th>
                    <th>SUBMITTED / PROCESSED</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedRows.map((r) => {
                    const isFulfilled = r.status === 'fulfilled';
                    const isRejected = r.status === 'rejected';
                    return (
                      <tr key={r.id}>
                        <td>
                          <span
                            className="badge"
                            style={{
                              background: isFulfilled ? 'rgba(0, 168, 107, 0.15)' : isRejected ? 'rgba(239, 68, 68, 0.15)' : 'rgba(249, 115, 22, 0.15)',
                              color: isFulfilled ? '#34d399' : isRejected ? '#f87171' : '#fb923c',
                              border: `1px solid ${isFulfilled ? 'rgba(0, 168, 107, 0.3)' : isRejected ? 'rgba(239, 68, 68, 0.3)' : 'rgba(249, 115, 22, 0.3)'}`,
                              textTransform: 'uppercase',
                            }}
                          >
                            {r.status || 'pending'}
                          </span>
                        </td>
                        <td>
                          <span style={{ textTransform: 'capitalize', fontWeight: 600 }}>{r.role || 'passenger'}</span>
                        </td>
                        <td>
                          <span style={{ fontWeight: 600, color: '#ffffff' }}>+91 {r.phone}</span>
                        </td>
                        <td className="mono" style={{ fontSize: 11, color: '#94a3b8' }}>
                          {r.uid || '—'}
                        </td>
                        <td>
                          <span style={{ fontSize: 12.5 }}>{REASON_LABELS[r.reason] || r.reason || '—'}</span>
                        </td>
                        <td className="mono" style={{ fontSize: 11 }}>
                          {formatTime(r.processedAt || r.createdAt)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
